import { IDashboardHomeCardMetricKey } from '~/src/dash/api/dashboards/home';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import { IDashboardChartMetric } from '~/src/dash/components/charts/common';
import { publicaColors } from '~/src/dash/utils/colors';

const colors = {
  controlBidFloor: publicaColors.lightGray,
  lowBidFloor: '#FF84EB',
  optimizedBidFloor: '#6553E8',
  highBidFloor: '#78DDAF',
};

export interface ISmartFloorsLiveConfig {
  defaultState: {
    displayedMetricKeys: IDashboardHomeCardMetricKey[];
    activeChartMetrics: IDashboardChartMetric[];
    activeCardBarMetricKey: IDashboardHomeCardMetricKey;
    activeCardLineMetricKey: IDashboardHomeCardMetricKey;
  };

  dropdownForBarMetrics: Record<IDashboardHomeCardMetricKey, IDashboardCard>;
  dropdownForLineMetrics: Record<IDashboardHomeCardMetricKey, IDashboardCard>;

  chartMetricsByBarMetricKey: { [key: string]: IDashboardChartMetric[] };
  chartMetricsByLineMetricKey: { [key: string]: IDashboardChartMetric };

  chartMetricsCfg: Record<IDashboardHomeCardMetricKey, IDashboardCard>;
}

export const smartFloorsLiveConfig: ISmartFloorsLiveConfig = {
  defaultState: {
    displayedMetricKeys: [
      'BidRequestsControl',
      'BidRequestsOptimized',
      'BidRequestsHigh',
      'BidRequestsLow',
      'BidFloorOptimized',
    ],
    activeChartMetrics: [
      {
        key: 'BidRequestsControl',
        label: 'Bid Requests Control',
        chartColorIndex: 0,
        customColor: colors.controlBidFloor,
        chartType: 'bar',
        valueType: 'integer',
        yScale: 'base',
      },
      {
        key: 'BidRequestsLow',
        label: 'Bid Requests Low',
        chartColorIndex: 5,
        customColor: colors.lowBidFloor,
        chartType: 'bar',
        valueType: 'integer',
        yScale: 'base',
      },
      {
        key: 'BidRequestsOptimized',
        label: 'Bid Requests Optimized',
        chartColorIndex: 2,
        customColor: colors.optimizedBidFloor, // TODO should these be different indexes for different colors?,
        chartType: 'bar',
        valueType: 'integer',
        yScale: 'base',
      },
      {
        key: 'BidRequestsHigh',
        label: 'Bid Requests High',
        chartColorIndex: 4,
        customColor: colors.highBidFloor,
        chartType: 'bar',
        valueType: 'integer',
        yScale: 'base',
      },
      {
        key: 'BidFloorOptimized',
        label: 'Bid Floor Optimized',
        chartColorIndex: 3,
        valueType: 'integer',
        chartType: 'line',
        yScale: 'small',
      },
    ],
    activeCardBarMetricKey: 'BidRequests',
    activeCardLineMetricKey: 'BidFloorOptimized',
  },

  chartMetricsByBarMetricKey: {
    Revenue: [
      {
        key: 'EstimatedRevenueControl',
        label: 'Estimated Revenue Control',
        chartColorIndex: 0,
        customColor: colors.controlBidFloor,
        valueType: 'currency',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'EstimatedRevenueLow',
        label: 'Estimated Revenue Low',
        chartColorIndex: 5,
        customColor: colors.lowBidFloor,
        valueType: 'currency',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'EstimatedRevenueOptimized',
        label: 'Estimated Revenue Optimized',
        chartColorIndex: 2,
        customColor: colors.optimizedBidFloor,
        valueType: 'currency',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'EstimatedRevenueHigh',
        label: 'Estimated Revenue High',
        chartColorIndex: 4,
        customColor: colors.highBidFloor,
        valueType: 'currency',
        chartType: 'bar',
        yScale: 'base',
      },
    ],
    BidRequests: [
      {
        key: 'BidRequestsControl',
        label: 'Bid Requests Control',
        chartColorIndex: 0,
        customColor: colors.controlBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'BidRequestsLow',
        label: 'Bid Requests Low',
        chartColorIndex: 5,
        customColor: colors.lowBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'BidRequestsOptimized',
        label: 'Bid Requests Optimized',
        chartColorIndex: 2,
        customColor: colors.optimizedBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'BidRequestsHigh',
        label: 'Bid Requests High',
        chartColorIndex: 4,
        customColor: colors.highBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
    ],
    BidResponses: [
      {
        key: 'BidResponsesControl',
        label: 'Bid Responses Control',
        chartColorIndex: 0,
        customColor: colors.controlBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'BidResponsesLow',
        label: 'Bid Responses Low',
        chartColorIndex: 5,
        customColor: colors.lowBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'BidResponsesOptimized',
        label: 'Bid Responses Optimized',
        chartColorIndex: 2,
        customColor: colors.optimizedBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'BidResponsesHigh',
        label: 'Bid Responses High',
        chartColorIndex: 4,
        customColor: colors.highBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
    ],
    CPM: [
      {
        key: 'CPMControl',
        label: 'CPM Control',
        chartColorIndex: 0,
        customColor: colors.controlBidFloor,
        valueType: 'currency_rate',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'CPMLow',
        label: 'CPM Low',
        chartColorIndex: 5,
        customColor: colors.lowBidFloor,
        valueType: 'currency_rate',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'CPMOptimized',
        label: 'CPM Optimized',
        chartColorIndex: 2,
        customColor: colors.optimizedBidFloor,
        valueType: 'currency_rate',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'CPMHigh',
        label: 'CPM High',
        chartColorIndex: 4,
        customColor: colors.highBidFloor,
        valueType: 'currency_rate',
        chartType: 'bar',
        yScale: 'base',
      },
    ],
    WinningBids: [
      {
        key: 'WinningBidsControl',
        label: 'Winning Bids Control',
        chartColorIndex: 0,
        customColor: colors.controlBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'WinningBidsLow',
        label: 'Winning Bids Low',
        chartColorIndex: 5,
        customColor: colors.lowBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'WinningBidsOptimized',
        label: 'Winning Bids Optimized',
        chartColorIndex: 2,
        customColor: colors.optimizedBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
      {
        key: 'WinningBidsHigh',
        label: 'Winning Bids High',
        chartColorIndex: 4,
        customColor: colors.highBidFloor,
        valueType: 'integer',
        chartType: 'bar',
        yScale: 'base',
      },
    ],
  },

  chartMetricsByLineMetricKey: {
    BidFloorControl: {
      key: 'BidFloorControl',
      label: 'Bid Floor Control',
      chartColorIndex: 3,
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },
    BidFloorOptimized: {
      key: 'BidFloorOptimized',
      label: 'Bid Floor Optimized',
      chartColorIndex: 3,
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },
    BidFloorHigh: {
      key: 'BidFloorHigh',
      label: 'Bid Floor High',
      chartColorIndex: 3,
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },
    BidFloorLow: {
      key: 'BidFloorLow',
      label: 'Bid Floor Low',
      chartColorIndex: 3,
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },
  },

  dropdownForBarMetrics: {
    Revenue: {
      key: 'Revenue',
      label: 'Estimated Revenue',
      responseKey: 'Revenue',
      trendKey: 'RevenueTrend',
      valueType: 'currency',
      chartType: 'bar',
      yScale: 'base',
    },
    CPM: {
      key: 'CPM',
      label: 'CPM',
      responseKey: 'CPM',
      trendKey: 'CPMTrend',
      valueType: 'currency_rate',
      chartType: 'line',
      yScale: 'base',
    },
    BidRequests: {
      key: 'BidRequests',
      label: 'Bid Requests',
      responseKey: 'BidRequests',
      trendKey: 'BidRequestsTrend',
      valueType: 'integer',
      chartType: 'line',
      yScale: 'base',
    },
    BidResponses: {
      key: 'BidResponses',
      label: 'Bid Responses',
      responseKey: 'BidResponses',
      trendKey: 'BidResponsesTrend',
      valueType: 'integer',
      chartType: 'line',
      yScale: 'base',
    },
    WinningBids: {
      key: 'WinningBids',
      label: 'Winning Bids',
      responseKey: 'WinningBids',
      trendKey: 'WinningBidsTrend',
      valueType: 'integer',
      chartType: 'line',
      yScale: 'base',
    },
  } as Record<IDashboardHomeCardMetricKey, IDashboardCard>,

  dropdownForLineMetrics: {
    BidFloorControl: {
      key: 'BidFloorControl',
      label: 'Bid Floor Control',
      responseKey: 'BidFloorControl',
      valueType: 'currency',
      chartType: 'line',
      yScale: 'small',
    },
    BidFloorOptimized: {
      key: 'BidFloorOptimized',
      label: 'Bid Floor Optimized',
      responseKey: 'BidFloorOptimized',
      valueType: 'currency_rate',
      chartType: 'line',
      yScale: 'small',
    },
    BidFloorHigh: {
      key: 'BidFloorHigh',
      label: 'Bid Floor High',
      responseKey: 'BidFloorHigh',
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },
    BidFloorLow: {
      key: 'BidFloorLow',
      label: 'Bid Floor Low',
      responseKey: 'BidFloorLow',
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },
  } as Record<IDashboardHomeCardMetricKey, IDashboardCard>,

  chartMetricsCfg: {
    // ALL metrics that come from realtime api

    // bid floors will be lines
    BidFloorControl: {
      key: 'BidFloorControl',
      label: 'Bid Floor Control',
      responseKey: 'BidFloorControl',
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },
    BidFloorOptimized: {
      key: 'BidFloorOptimized',
      label: 'Bid Floor Optimized',
      responseKey: 'BidFloorOptimized',
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },
    BidFloorHigh: {
      key: 'BidFloorHigh',
      label: 'Bid Floor High',
      responseKey: 'BidFloorHigh',
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },
    BidFloorLow: {
      key: 'BidFloorOptimized',
      label: 'Bid Floor Low',
      responseKey: 'BidFloorLow',
      valueType: 'integer',
      chartType: 'line',
      yScale: 'small',
    },

    // everything else will be bar
    EstimatedRevenueControl: {
      key: 'EstimatedRevenueControl',
      label: 'Estimated Revenue Control',
      responseKey: 'EstimatedRevenueControl',
      valueType: 'currency',
      chartType: 'bar',
      yScale: 'base',
    },
    EstimatedRevenueOptimized: {
      key: 'EstimatedRevenueOptimized',
      label: 'Estimated Revenue Optimized',
      responseKey: 'EstimatedRevenueOptimized',
      valueType: 'currency',
      chartType: 'bar',
      yScale: 'base',
    },
    EstimatedRevenueLow: {
      key: 'EstimatedRevenueLow',
      label: 'Estimated Revenue Low',
      responseKey: 'EstimatedRevenueLow',
      valueType: 'currency',
      chartType: 'bar',
      yScale: 'base',
    },
    EstimatedRevenueHigh: {
      key: 'EstimatedRevenueHigh',
      label: 'Estimated Revenue High',
      responseKey: 'EstimatedRevenueHigh',
      valueType: 'currency',
      chartType: 'bar',
      yScale: 'base',
    },

    CPMControl: {
      key: 'CPMControl',
      label: 'CPM Control',
      responseKey: 'CPMControl',
      valueType: 'currency_rate',
      chartType: 'bar',
      yScale: 'base',
    },
    CPMOptimized: {
      key: 'CPMOptimized',
      label: 'CPM Optimized',
      responseKey: 'CPMOptimized',
      valueType: 'currency_rate',
      chartType: 'bar',
      yScale: 'base',
    },
    CPMHigh: {
      key: 'CPMHigh',
      label: 'CPM High',
      responseKey: 'CPMHigh',
      valueType: 'currency_rate',
      chartType: 'bar',
      yScale: 'base',
    },
    CPMLow: {
      key: 'CPMLow',
      label: 'CPM Low',
      responseKey: 'CPMLow',
      valueType: 'currency_rate',
      chartType: 'bar',
      yScale: 'base',
    },

    BidRequestsControl: {
      key: 'BidRequestsControl',
      label: 'Bid Requests Control',
      responseKey: 'BidRequestsControl',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
    BidRequestsOptimized: {
      key: 'BidRequestsOptimized',
      label: 'Bid Requests Optimized',
      responseKey: 'BidRequestsOptimized',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
    BidRequestsHigh: {
      key: 'BidRequestsHigh',
      label: 'Bid Requests High',
      responseKey: 'BidRequestsHigh',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
    BidRequestsLow: {
      key: 'BidRequestsLow',
      label: 'Bid Requests Low',
      responseKey: 'BidRequestsLow',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },

    BidResponsesControl: {
      key: 'BidResponsesControl',
      label: 'Bid Responses Control',
      responseKey: 'BidResponsesControl',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
    BidResponsesOptimized: {
      key: 'BidResponsesOptimized',
      label: 'Bid Responses Optimized',
      responseKey: 'BidResponsesOptimized',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
    BidResponsesHigh: {
      key: 'BidResponsesHigh',
      label: 'Bid Responses High',
      responseKey: 'BidResponsesHigh',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
    BidResponsesLow: {
      key: 'BidResponsesLow',
      label: 'Bid Responses Low',
      responseKey: 'BidResponsesLow',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },

    WinningBidsControl: {
      key: 'WinningBidsControl',
      label: 'Winning Bids Control',
      responseKey: 'WinningBids',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
    WinningBidsOptimized: {
      key: 'WinningBidsOptimized',
      label: 'Winning Bids Optimized',
      responseKey: 'WinningBidsOptimized',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
    WinningBidsHigh: {
      key: 'WinningBidsHigh',
      label: 'Winning Bids High',
      responseKey: 'WinningBidsHigh',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
    WinningBidsLow: {
      key: 'WinningBidsLow',
      label: 'Winning Bids Low',
      responseKey: 'WinningBidsLow',
      valueType: 'integer',
      chartType: 'bar',
      yScale: 'base',
    },
  } as Record<IDashboardHomeCardMetricKey, IDashboardCard>,
};
